import { useMemo, useState } from 'react'
import {
  format,
  startOfWeek,
  subWeeks,
  endOfWeek,
  addWeeks,
  isSameWeek,
} from 'date-fns'
import { motion, Variants } from 'framer-motion'
import {
  TrendingUp,
  BarChart3,
  PieChart,
  Briefcase,
  Users,
  CheckCircle2,
  ArrowUpRight,
  Target,
  Zap,
  ChevronLeft,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Info,
  Layers,
  Award,
} from 'lucide-react'

import { useGetTeamsQuery } from '@/redux/service/employee'
import { useGetWPPlansQuery, WPPlanRow } from '@/redux/service/weeklyPlanning'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

function getMonday(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 1 })
}

function formatDateStr(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

function formatWeekLabel(monday: Date): string {
  const sunday = endOfWeek(monday, { weekStartsOn: 1 })
  if (monday.getMonth() === sunday.getMonth()) {
    return `${format(monday, 'd')} – ${format(sunday, 'd MMM yyyy')}`
  }
  return `${format(monday, 'd MMM')} – ${format(sunday, 'd MMM yyyy')}`
}

function sumField(rows: WPPlanRow[] = [], field: keyof WPPlanRow): number {
  return rows.reduce(
    (acc, r) => acc + (Number.parseFloat(String(r[field])) || 0),
    0
  )
}

function InsightRow({
  label,
  value,
  subValue,
  icon: Icon,
  formula,
  color = 'primary',
}: {
  label: string
  value: string
  subValue?: string
  icon?: React.ElementType
  formula?: string
  color?: 'primary' | 'blue' | 'purple' | 'green'
}) {
  const textColorMap = {
    primary: 'text-primary',
    blue: 'text-blue-500',
    purple: 'text-purple-500',
    green: 'text-green-500',
  }

  const borderColorMap = {
    primary: 'border-primary/30',
    blue: 'border-blue-500/30',
    purple: 'border-purple-500/30',
    green: 'border-green-500/30',
  }

  const bgColorMap = {
    primary: 'bg-primary/5',
    blue: 'bg-blue-500/5',
    purple: 'bg-purple-500/5',
    green: 'bg-green-500/5',
  }

  return (
    <div className='flex items-center justify-between p-4 rounded-3xl bg-accent/20 border border-border/40 hover:bg-accent/40 transition-all group relative overflow-hidden'>
      <div
        className={cn(
          'absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500',
          bgColorMap[color]
        )}
      />
      <div className='flex items-center gap-3 relative z-10'>
        {Icon && (
          <div
            className={cn(
              'p-2.5 rounded-2xl bg-background shadow-sm group-hover:scale-110 transition-transform duration-500',
              textColorMap[color]
            )}
          >
            <Icon size={16} strokeWidth={2.5} />
          </div>
        )}
        <div className='flex flex-col'>
          <div className='flex items-center gap-1.5'>
            <span className='text-[13px] font-bold text-muted-foreground/80'>
              {label}
            </span>
            {formula && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info
                      size={12}
                      className={cn(
                        'opacity-30 hover:opacity-100 transition-opacity',
                        textColorMap[color]
                      )}
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    className={cn(
                      'bg-background/95 backdrop-blur-xl border shadow-2xl p-3 max-w-[200px]',
                      borderColorMap[color]
                    )}
                  >
                    <p
                      className={cn(
                        'text-[10px] font-black uppercase tracking-[0.1em] mb-1.5',
                        textColorMap[color]
                      )}
                    >
                      Logic Guide
                    </p>
                    <p className='text-[11px] leading-relaxed font-semibold text-foreground/90'>
                      {formula}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>
      <div className='text-right relative z-10'>
        <div className='text-sm font-black tracking-tight text-foreground'>
          {value}
        </div>
        {subValue && (
          <div
            className={cn(
              'text-[10px] font-black uppercase tracking-widest mt-0.5',
              textColorMap[color]
            )}
          >
            {subValue}
          </div>
        )}
      </div>
    </div>
  )
}

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  color = 'primary',
  formula,
}: {
  title: string
  value: string
  subtitle: string
  icon: React.ElementType
  trend?: string | undefined
  color?: 'primary' | 'blue' | 'purple' | 'green'
  formula?: string
}) {
  const colorMap = {
    primary: 'bg-primary/10 text-primary border-primary/20',
    blue: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    purple: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    green: 'bg-green-500/10 text-green-500 border-green-500/20',
  }

  const textColorMap = {
    primary: 'text-primary',
    blue: 'text-blue-500',
    purple: 'text-purple-500',
    green: 'text-green-500',
  }

  const borderColorMap = {
    primary: 'border-primary/30',
    blue: 'border-blue-500/30',
    purple: 'border-purple-500/30',
    green: 'border-green-500/30',
  }

  return (
    <motion.div
      whileHover={{ y: -6, scale: 1.01 }}
      className='relative overflow-hidden rounded-[2.5rem] border border-border/50 bg-card/40 backdrop-blur-2xl p-7 shadow-sm hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] transition-all duration-500'
    >
      <div className='flex justify-between items-start mb-6'>
        <div
          className={cn('p-4 rounded-3xl border shadow-inner', colorMap[color])}
        >
          <Icon size={26} strokeWidth={2.5} />
        </div>
        {trend && (
          <div className='flex items-center gap-1.5 px-3 py-1.5 rounded-2xl bg-green-500/10 text-green-500 text-[10px] font-black uppercase tracking-widest border border-green-500/20'>
            <ArrowUpRight size={14} strokeWidth={3} />
            {trend}
          </div>
        )}
      </div>
      <div className='space-y-1'>
        <div className='flex items-center gap-2'>
          <h3 className='text-[11px] font-black text-muted-foreground/60 uppercase tracking-[0.15em]'>
            {title}
          </h3>
          {formula && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info
                    size={14}
                    className={cn(
                      'opacity-30 hover:opacity-100 transition-all cursor-help',
                      textColorMap[color]
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent
                  className={cn(
                    'bg-background/95 backdrop-blur-xl border shadow-2xl p-4 max-w-[220px]',
                    borderColorMap[color]
                  )}
                >
                  <p
                    className={cn(
                      'text-[10px] font-black uppercase tracking-[0.1em] mb-2',
                      textColorMap[color]
                    )}
                  >
                    Calculation Logic
                  </p>
                  <p className='text-[11px] leading-relaxed font-semibold text-foreground/90'>
                    {formula}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className='text-4xl font-black tracking-tighter text-foreground'>
          {value}
        </div>
        <p className='text-xs font-bold text-muted-foreground/50 mt-2 flex items-center gap-1.5'>
          <span
            className={cn(
              'w-1 h-1 rounded-full',
              textColorMap[color].replace('text', 'bg')
            )}
          />
          {subtitle}
        </p>
      </div>
      <div
        className={cn(
          'absolute -right-6 -bottom-6 w-32 h-32 opacity-[0.04] rotate-12 transition-transform duration-700 group-hover:scale-110 group-hover:rotate-0',
          textColorMap[color]
        )}
      >
        <Icon size={128} strokeWidth={1} />
      </div>
    </motion.div>
  )
}

export default function WeeklyResourcePlanningDashboard() {
  const [selectedMonday, setSelectedMonday] = useState<Date>(() =>
    getMonday(new Date())
  )
  const [calOpen, setCalOpen] = useState(false)

  const weekStart = selectedMonday
  const isCurrentWeek = isSameWeek(weekStart, new Date(), { weekStartsOn: 1 })

  // Current Week
  const { data: currentPlans, isLoading: isCurrentLoading } =
    useGetWPPlansQuery({
      week_start: formatDateStr(weekStart),
    })

  // Previous Week
  const prevWeekStart = getMonday(subWeeks(weekStart, 1))
  const { data: prevPlans, isLoading: isPrevLoading } = useGetWPPlansQuery({
    week_start: formatDateStr(prevWeekStart),
  })

  useGetTeamsQuery()

  const currentRows = useMemo(
    () => (currentPlans?.success ? (currentPlans.data ?? []) : []),
    [currentPlans]
  )

  const prevRows = useMemo(
    () => (prevPlans?.success ? (prevPlans.data ?? []) : []),
    [prevPlans]
  )

  // Current Metrics
  const totalPlannedBilling = useMemo(
    () => sumField(currentRows, 'est_billing_hrs'),
    [currentRows]
  )

  const totalAchievedBillable = useMemo(
    () => sumField(currentRows, 'achieved_billable_hrs'),
    [currentRows]
  )

  const estimatedCapacity = useMemo(
    () => currentRows.length * 45,
    [currentRows.length]
  )

  const plannedUtilization = useMemo(
    () =>
      estimatedCapacity > 0
        ? Math.min((totalPlannedBilling / estimatedCapacity) * 100, 100)
        : 0,
    [totalPlannedBilling, estimatedCapacity]
  )

  const currentAchievementRate = useMemo(
    () =>
      totalPlannedBilling > 0
        ? Math.min((totalAchievedBillable / totalPlannedBilling) * 100, 100)
        : 0,
    [totalAchievedBillable, totalPlannedBilling]
  )

  // Previous Metrics
  const prevPlannedBilling = useMemo(
    () => sumField(prevRows, 'est_billing_hrs'),
    [prevRows]
  )

  const prevAchievedBilling = useMemo(
    () => sumField(prevRows, 'achieved_billable_hrs'),
    [prevRows]
  )

  const prevCapacity = useMemo(() => prevRows.length * 45, [prevRows.length])

  const achievementRate = useMemo(
    () =>
      prevPlannedBilling > 0
        ? Math.min((prevAchievedBilling / prevPlannedBilling) * 100, 100)
        : 0,
    [prevAchievedBilling, prevPlannedBilling]
  )

  const prevCapacityAchieved = useMemo(
    () =>
      prevCapacity > 0
        ? Math.min((prevAchievedBilling / prevCapacity) * 100, 100)
        : 0,
    [prevAchievedBilling, prevCapacity]
  )

  // Department Level Metrics
  const deptPrev = useMemo(() => {
    const map: Record<
      string,
      {
        planned: number
        achieved: number
        capacity: number
        memberCount: number
      }
    > = {}

    prevRows.forEach(r => {
      const team = r.team || 'Unassigned'
      const planned = Number.parseFloat(String(r.est_billing_hrs)) || 0
      const achieved = Number.parseFloat(String(r.achieved_billable_hrs)) || 0

      if (!map[team]) {
        map[team] = { planned: 0, achieved: 0, capacity: 0, memberCount: 0 }
      }

      map[team].planned += planned
      map[team].achieved += achieved
      map[team].memberCount += 1
    })

    // Set capacity as memberCount * 45
    Object.values(map).forEach(d => {
      d.capacity = d.memberCount * 45
    })

    return map
  }, [prevRows])

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  }

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', damping: 25, stiffness: 400 },
    },
  }

  if (isCurrentLoading || isPrevLoading) {
    return (
      <div className='flex h-[60vh] items-center justify-center bg-background'>
        <div className='relative flex flex-col items-center gap-6'>
          <div className='h-20 w-20 rounded-full border-4 border-primary/10 border-t-primary animate-spin shadow-xl' />
          <div className='space-y-1 text-center'>
            <p className='text-sm font-black uppercase tracking-widest text-foreground animate-pulse'>
              Syncing Performance Data
            </p>
            <p className='text-[10px] font-bold text-muted-foreground uppercase opacity-60'>
              Please wait while we gather insights
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      variants={containerVariants}
      initial='hidden'
      animate='visible'
      className='space-y-10 pb-10'
    >
      {/* Background blobs for depth */}
      <div className='fixed inset-0 overflow-hidden pointer-events-none'>
        <div className='absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]' />
        <div className='absolute top-[20%] -right-[10%] w-[30%] h-[30%] bg-blue-500/5 rounded-full blur-[100px]' />
      </div>

      <div className='flex flex-col md:flex-row md:items-center justify-between gap-8 pb-8 border-b border-border/50 relative z-10'>
        <div className='flex items-center gap-4 relative z-20'>
          <div className='flex items-center gap-1.5 bg-card/60 backdrop-blur-xl rounded-[22px] border border-border/50 p-1.5 shadow-sm'>
            <Button
              variant='ghost'
              size='icon'
              className='h-10 w-10 rounded-2xl hover:bg-accent text-muted-foreground shrink-0 transition-all'
              onClick={() =>
                setSelectedMonday(prev => getMonday(subWeeks(prev, 1)))
              }
            >
              <ChevronLeft size={18} strokeWidth={2.5} />
            </Button>

            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button className='flex items-center justify-center gap-2.5 px-5 py-2 text-[13px] font-black text-foreground hover:bg-accent rounded-2xl transition-all duration-300 min-w-0'>
                  <CalendarDays size={16} className='text-primary' />
                  <span className='truncate tracking-tight'>
                    {formatWeekLabel(selectedMonday)}
                  </span>
                  <ChevronDown size={14} className='text-muted-foreground/60' />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto p-0 bg-background/95 backdrop-blur-3xl border-border shadow-2xl rounded-[32px] overflow-hidden z-[100]'
                align='end'
              >
                <div className='p-2'>
                  <Calendar
                    mode='single'
                    captionLayout='dropdown'
                    selected={selectedMonday}
                    onSelect={d => {
                      if (d) {
                        setSelectedMonday(getMonday(d))
                        setCalOpen(false)
                      }
                    }}
                    modifiers={{
                      weekHighlight: d =>
                        isSameWeek(d, selectedMonday, { weekStartsOn: 1 }),
                    }}
                    modifiersClassNames={{
                      weekHighlight:
                        '!bg-primary/20 !text-primary !rounded-none first:!rounded-l-xl last:!rounded-r-xl font-black',
                    }}
                    initialFocus
                  />
                  <div className='p-2 pt-0'>
                    <Button
                      variant='outline'
                      className='w-full rounded-2xl font-black text-[11px] uppercase tracking-widest border-border/50 hover:bg-primary hover:text-primary-foreground transition-all'
                      onClick={() => {
                        setSelectedMonday(getMonday(new Date()))
                        setCalOpen(false)
                      }}
                    >
                      Return to current week
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <Button
              variant='ghost'
              size='icon'
              className='h-10 w-10 rounded-2xl hover:bg-accent text-muted-foreground shrink-0 transition-all'
              onClick={() =>
                setSelectedMonday(prev => getMonday(addWeeks(prev, 1)))
              }
            >
              <ChevronRight size={18} strokeWidth={2.5} />
            </Button>
          </div>
          {!isCurrentWeek && (
            <Button
              variant='outline'
              className='h-12 px-6 rounded-[22px] text-[11px] font-black uppercase tracking-widest border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground shadow-lg shadow-primary/5 transition-all'
              onClick={() => setSelectedMonday(getMonday(new Date()))}
            >
              Today
            </Button>
          )}
        </div>
      </div>

      {/* Primary Metrics Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10'>
        <MetricCard
          title='Total Planned Billing'
          value={`~${totalPlannedBilling.toFixed(1)}h`}
          subtitle='Total Planned Billing output'
          icon={Briefcase}
          trend={
            totalPlannedBilling > prevPlannedBilling ? 'Rising' : undefined
          }
          formula='Sum of all "Estimated Billing Hours" entered by team members for this week.'
        />
        <MetricCard
          title='Estimated Capacity'
          value={`~${estimatedCapacity.toFixed(1)}h`}
          subtitle='Based on current head count'
          icon={Users}
          color='blue'
          formula='Calculation: (Active unique team members) × (45 Standard hours per week).'
        />
        <MetricCard
          title='Planned Utilization'
          value={`${plannedUtilization.toFixed(2)}%`}
          subtitle='Utilization vs Total Bandwidth'
          icon={Award}
          color='purple'
          trend={plannedUtilization > 0 ? 'Improving' : undefined}
          formula='Efficiency = (Total Planned Billing / Total Estimated Capacity) × 100'
        />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-12 gap-8 relative z-10'>
        {/* Previous Week Performance */}
        <motion.div variants={itemVariants} className='lg:col-span-4'>
          <Card className='border-none bg-card/40 backdrop-blur-2xl rounded-[3rem] overflow-hidden ring-1 ring-border/50 h-full shadow-lg'>
            <div className='px-8 py-7 border-b border-border/40 bg-accent/20'>
              <div className='flex items-center gap-4'>
                <div className='p-3 rounded-2xl bg-blue-500/10 text-blue-500 shadow-inner'>
                  <BarChart3 size={22} strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className='text-[11px] font-black uppercase tracking-[0.2em] text-foreground/80'>
                    Performance
                  </h3>
                  <p className='text-[10px] font-bold text-muted-foreground uppercase tracking-tight opacity-60'>
                    Week {format(subWeeks(weekStart, 1), 'w')} Results
                  </p>
                </div>
              </div>
            </div>
            <CardContent className='p-8 space-y-5'>
              <InsightRow
                label='Total Planned Billing'
                value={`${prevPlannedBilling.toFixed(1)} hrs`}
                icon={Target}
                color='blue'
              />
              <InsightRow
                label='Total Achieved Billing'
                value={`${prevAchievedBilling.toFixed(1)} hrs`}
                icon={CheckCircle2}
                color='blue'
              />
              <div className='pt-2 space-y-4'>
                <InsightRow
                  label='Planned vs Achieved'
                  value={`${achievementRate.toFixed(2)}%`}
                  subValue='Target Fulfillment'
                  icon={PieChart}
                  formula='Achievement = (Actual Billed Hours / Planned Hours) × 100'
                  color='blue'
                />
                <InsightRow
                  label='Capacity vs Achieved'
                  value={`${prevCapacityAchieved.toFixed(2)}%`}
                  subValue='Resource Yield'
                  icon={Zap}
                  formula='Utilization = (Actual Billed Hours / (Members × 45h)) × 100'
                  color='blue'
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Current Planned Insights */}
        <motion.div variants={itemVariants} className='lg:col-span-4'>
          <Card className='border-none bg-card/40 backdrop-blur-2xl rounded-[3rem] overflow-hidden ring-1 ring-border/50 h-full shadow-lg'>
            <div className='px-8 py-7 border-b border-border/40 bg-accent/20'>
              <div className='flex items-center gap-4'>
                <div className='p-3 rounded-2xl bg-purple-500/10 text-purple-500 shadow-inner'>
                  <Layers size={22} strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className='text-[11px] font-black uppercase tracking-[0.2em] text-foreground/80'>
                    Forecast
                  </h3>
                  <p className='text-[10px] font-bold text-muted-foreground uppercase tracking-tight opacity-60'>
                    Current Week Outlook
                  </p>
                </div>
              </div>
            </div>
            <CardContent className='p-8 space-y-5'>
              <InsightRow
                label='Total Planned Billing'
                value={`${totalPlannedBilling.toFixed(1)} hrs`}
                icon={Briefcase}
                formula='Current total of all estimated billable hours.'
                color='purple'
              />
              <InsightRow
                label='Total Capacity'
                value={`~${estimatedCapacity.toFixed(1)} hrs`}
                icon={Users}
                formula='Theoretical max based on active head count (45h/member).'
                color='purple'
              />
              <InsightRow
                label='Planned Utilization'
                value={`${plannedUtilization.toFixed(2)}%`}
                icon={TrendingUp}
                formula='Allocation intensity relative to total bandwidth.'
                color='purple'
              />
              <div className='pt-6'>
                <div className='p-8 rounded-[2.5rem] bg-primary text-primary-foreground shadow-2xl shadow-primary/20 space-y-3 relative overflow-hidden group'>
                  <div className='absolute top-0 right-0 p-6 opacity-10 group-hover:scale-125 group-hover:rotate-12 transition-all duration-700'>
                    <Award size={80} strokeWidth={1} />
                  </div>
                  <h4 className='text-[10px] font-black uppercase tracking-[0.25em] opacity-70'>
                    This Week Billing Estimation
                  </h4>
                  <div className='text-3xl font-black '>
                    {totalAchievedBillable.toFixed(1)} /{' '}
                    {totalPlannedBilling.toFixed(1)}{' '}
                    <span className='text-sm opacity-60'>hrs</span>
                  </div>
                  <div className='text-[10px] font-black bg-white/20 w-fit px-3 py-1.5 rounded-xl backdrop-blur-md uppercase tracking-widest border border-white/10'>
                    {currentAchievementRate.toFixed(2)}% Goal Progress
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Department Level Breakdown */}
        <motion.div variants={itemVariants} className='lg:col-span-4'>
          <Card className='border-none bg-card/40 backdrop-blur-2xl rounded-[3rem] overflow-hidden ring-1 ring-border/50 h-full shadow-lg'>
            <div className='px-8 py-7 border-b border-border/40 bg-accent/20'>
              <div className='flex items-center gap-4'>
                <div className='p-3 rounded-2xl bg-green-500/10 text-green-500 shadow-inner'>
                  <Users size={22} strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className='text-[11px] font-black uppercase tracking-[0.2em] text-foreground/80'>
                    Departments
                  </h3>
                  <p className='text-[10px] font-bold text-muted-foreground uppercase tracking-tight opacity-60'>
                    Unit Performance Review
                  </p>
                </div>
              </div>
            </div>
            <CardContent className='p-6 h-[460px] overflow-y-auto custom-scrollbar space-y-6'>
              <div className='space-y-6'>
                {Object.entries(deptPrev).length > 0 ? (
                  Object.entries(deptPrev).map(([dept, data]) => {
                    const achievedVsPlanned =
                      data.planned > 0
                        ? (data.achieved / data.planned) * 100
                        : 0
                    const achievedVsCapacity =
                      data.capacity > 0
                        ? (data.achieved / data.capacity) * 100
                        : 0

                    return (
                      <div
                        key={dept}
                        className='group space-y-5 p-6 rounded-[2.5rem] bg-accent/20 border border-border/40 hover:border-primary/30 transition-all duration-500'
                      >
                        <div className='flex items-center justify-between'>
                          <span className='text-[13px] font-black uppercase tracking-widest text-foreground/90'>
                            {dept}
                          </span>
                          <div className='flex items-center gap-2 bg-background/50 px-3 py-1 rounded-full shadow-sm border border-border/50'>
                            <Users size={10} className='text-primary' />
                            <span className='text-[9px] font-black text-muted-foreground uppercase'>
                              {data.memberCount} Staff
                            </span>
                          </div>
                        </div>

                        <div className='space-y-6'>
                          {/* Vs Planned */}
                          <div className='space-y-2.5'>
                            <div className='flex justify-between items-end'>
                              <div className='flex flex-col gap-0.5'>
                                <span className='text-[9px] font-black text-muted-foreground/60 uppercase tracking-widest'>
                                  Fulfillment
                                </span>
                                <span className='text-[10px] font-black text-foreground'>
                                  {achievedVsPlanned.toFixed(2)}%
                                </span>
                              </div>
                              <div className='text-[10px] font-black text-primary/80 bg-primary/5 px-2 py-0.5 rounded-lg border border-primary/10'>
                                {data.achieved.toFixed(1)} /{' '}
                                {data.planned.toFixed(1)}{' '}
                                <span className='text-[8px] opacity-60'>h</span>
                              </div>
                            </div>
                            <div className='w-full h-1.5 bg-muted/50 rounded-full overflow-hidden p-[1px]'>
                              <div
                                className='h-full bg-primary rounded-full transition-all duration-1000 ease-out'
                                style={{
                                  width: `${Math.min(achievedVsPlanned, 100)}%`,
                                }}
                              />
                            </div>
                          </div>

                          {/* Vs Capacity */}
                          <div className='space-y-2.5'>
                            <div className='flex justify-between items-end'>
                              <div className='flex flex-col gap-0.5'>
                                <span className='text-[9px] font-black text-muted-foreground/60 uppercase tracking-widest'>
                                  Utilization
                                </span>
                                <span className='text-[10px] font-black text-blue-500'>
                                  {achievedVsCapacity.toFixed(2)}%
                                </span>
                              </div>
                              <div className='text-[10px] font-black text-blue-500/80 bg-blue-500/5 px-2 py-0.5 rounded-lg border border-blue-500/10'>
                                {data.achieved.toFixed(1)} /{' '}
                                {data.capacity.toFixed(1)}{' '}
                                <span className='text-[8px] opacity-60'>h</span>
                              </div>
                            </div>
                            <div className='w-full h-1.5 bg-muted/50 rounded-full overflow-hidden p-[1px]'>
                              <div
                                className='h-full bg-blue-500 rounded-full transition-all duration-1000 ease-out'
                                style={{
                                  width: `${Math.min(achievedVsCapacity, 100)}%`,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className='flex flex-col items-center justify-center h-[300px] text-muted-foreground gap-4'>
                    <div className='p-6 rounded-full bg-accent/20 border border-dashed border-border animate-pulse'>
                      <Layers size={40} className='opacity-20' />
                    </div>
                    <p className='text-[11px] font-black uppercase tracking-[0.2em] opacity-40'>
                      Analytical Void Detected
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  )
}
